import request from 'api/requestUtils'
import { getDomainUserId } from 'analytics/snowplow/helpers/domainUserId'
import { paths, definitions } from '@luxuryescapes/contract-cart'
import { reportClientError } from 'services/errorReportingService'

export type CartItem = definitions['cartItem']
export type CartRequestV2 = paths['/api/carts/{version}/{cartId}']['put']['parameters']['body']['payload']
export type Brand = CartRequestV2['brand']
export type UserCartRequest = paths['/api/carts/{version}/mycart']['put']['parameters']['body']['payload']
export type UserCartResponse = paths['/api/carts/{version}/mycart']['get']['responses']['200']['content']['application/json']

export type TermsAndConditionsUpdateData = {
  orderId?: string,
  accepted?: boolean,
  acceptedAt?: string,
}

export type CartItemResponse = {
  items: Array<App.Checkout.AnyItem>
  total: number | undefined
  type: 'pending' | 'completed'
}
export type CartTemplateResponse = {
  id: string,
  template: {
    items: Array<CartItem>
  },
  createdAt: string
  updatedAt: string
}

export type QuoteDedupeKeys = {
  customerEmailDedupeKey: string
  multipleCustomerEmailDedupeKey?: string
}

type CartSuccessResponseV2 = paths['/api/carts/{version}/{cartId}']['put']['responses']['200']
type UserCartResponseState = Omit<App.UserCartState, 'synced' | 'fetching'>

export function finalizeCart(transactionKey: string) {
  return request.patch(`/api/carts/${transactionKey}`, { op: 'finalize' }, { credentials: 'include' })
}

export function putNewCheckoutCart(transactionKey: string, cart:CartRequestV2) {
  return request.put<CartSuccessResponseV2, CartRequestV2>(`/api/carts/v2/${transactionKey}`, { ...cart, domainUserId: getDomainUserId() }, { credentials: 'include' })
    .catch((err) => reportClientError('PUT Cart Request Failed', {
      cartPayloadItems: cart.items,
      transactionKey,
      error: err.message,
    }))
}

export function getCartItems(cartId: string): Promise<CartItemResponse> {
  return request.get<CartItemResponse>(`/api/carts/v2/${cartId}/items`, { credentials: 'include' }).then((res): CartItemResponse => ({
    items: res.items,
    total: res.total,
    type: res.type ?? 'pending',
  }))
}

export function createCartTemplate(items: Array<App.Checkout.AnyItem>): Promise<CartTemplateResponse> {
  return request.post('/api/carts/templates?without_brand=1', { items }, { credentials: 'include' })
}

export function createQuote(cartId: string, callbackDate?: string) {
  return request.post('/api/carts/quotes', { cartId, callbackDate }, { credentials: 'include' })
}

export function saleAttribution(cartId: string, orderId: string) {
  return request.post('/api/carts/attribution?without_brand=1', { cartId, orderId }, { credentials: 'include' })
}

export function updateQuote(quoteId: string, keys: QuoteDedupeKeys, callbackDate?: string) {
  return request.patch(`/api/carts/quotes/${quoteId}?without_brand=1`, { ...keys, callbackDate }, { credentials: 'include' })
}

// TODO map contract at item level
const mapUserCartResponse = (response: UserCartResponse):UserCartResponseState => {
  const items: App.UserCartState['items'] = {}
  const itemStates: App.UserCartState['itemStates'] = {}

  response.result.items?.forEach(item => {
    if ('saveForLater' in item) {
      const { saveForLater, ...rest } = item
      items[item.itemId] = rest as App.Checkout.AnyItem
      itemStates[item.itemId] = { deleted: false, saveForLater: !!saveForLater }
    } else {
      items[item.itemId] = item as App.Checkout.AnyItem
      itemStates[item.itemId] = { deleted: false, saveForLater: false }
    }
  })

  return {
    items,
    itemStates,
    roomsToBeConnected: response.result.connectedRooms ?? [],
  }
}
export async function getUserCart():Promise<UserCartResponseState> {
  return mapUserCartResponse(await request.get<UserCartResponse>('/api/carts/v2/mycart', { credentials: 'include' }))
}

// TODO map contract at item level
const mapUserCartRequest = (userCartState: App.UserCartState) => {
  return {
    items: Object.values(userCartState.items)
      .filter(item => !userCartState.itemStates[item.itemId].deleted)
      .map(item => ({
        ...item,
        saveForLater: userCartState.itemStates[item.itemId].saveForLater,
      })),
    connectedRooms: userCartState.roomsToBeConnected,
  }
}

export function putUserCart(userCartState: App.UserCartState) {
  return request.put('/api/carts/v2/mycart', mapUserCartRequest(userCartState), { credentials: 'include' })
}

export function createTermsAndConditions(cartId: string, emailId: string) {
  return request.post('/api/carts/v1/terms-and-conditions', { cartId, emailId }, { credentials: 'include' })
}

export function updateTermsAndConditions(cartId: string, updateData: TermsAndConditionsUpdateData) {
  return request.patch(`/api/carts/v1/terms-and-conditions/${cartId}`, updateData, { credentials: 'include' })
}
